import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { Link } from "react-router-dom";

const chapters = [
  {
    no: "01",
    year: "2023",
    title: "Um quarto livre, um sonho antigo",
    body:
      "Mariana deixou o emprego corporativo em abril. Em junho, o quarto de hóspedes já tinha duas mesas de trabalho, pilhas de barro e exatamente uma cliente — a mãe dela.",
  },
  {
    no: "02",
    year: "2024",
    title: "Duas mãos a mais",
    body:
      "Julia entrou para modelar cerâmica. Pedro chegou para tingir linho na pia da cozinha. Em seis meses, o quarto já não dava mais conta.",
  },
  {
    no: "03",
    year: "2025",
    title: "Um ateliê de verdade, finalmente",
    body:
      "Uma antiga gráfica no centro de Goiânia. Piso de cimento, janelas voltadas para o oeste, espaço para três bancadas e um forno de cerâmica.",
  },
];

const numbers = [
  { value: "3", label: "Artesãs" },
  { value: "640", label: "Peças enviadas" },
  { value: "12", label: "Materiais que usamos" },
  { value: "0", label: "Fábricas" },
];

export default function StoryPage() {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Masthead */}
        <section className="w-full px-4 md:px-8 pt-10 md:pt-14 pb-10">
          <div className="max-w-[1180px] mx-auto">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-foreground-500 border-b border-foreground-900/15 pb-3 mb-10">
              <span>Nossa História · Edição 03</span>
              <span>Goiânia, GO</span>
            </div>

            <p className="text-[13px] uppercase tracking-[0.24em] text-primary-600 mb-6">
              Três artesãs, um ateliê
            </p>
            <h1 className="font-heading text-foreground-950 text-[56px] md:text-[88px] lg:text-[112px] leading-[0.92] tracking-[-0.03em] mb-8">
              Como<br />
              <em className="text-primary-600 not-italic font-normal italic">chegamos aqui.</em>
            </h1>
            <p className="text-[17px] md:text-[19px] leading-relaxed text-foreground-700 max-w-2xl">
              A Casa Flor começou num quarto de hóspedes com um vaso de barro e muita dúvida.
              Três anos depois, somos três pessoas fazendo coisas com calma num canto de Goiânia.
            </p>
          </div>
        </section>

        {/* Cover image */}
        <section className="w-full px-4 md:px-8 pb-16">
          <div className="max-w-[1180px] mx-auto">
            <div className="relative w-full aspect-[16/8] rounded-md overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Editorial%20documentary%20photo%20of%20three%20Brazilian%20women%20artisans%20working%20in%20a%20sunlit%20concrete%20studio%20with%20wooden%20benches%20and%20a%20ceramic%20kiln%2C%20one%20woman%20shaping%20clay%20at%20a%20pottery%20wheel%2C%20another%20weaving%20natural%20fibers%2C%20the%20third%20arranging%20handmade%20ceramic%20pieces%20on%20a%20shelf%2C%20warm%20natural%20window%20light%2C%20muted%20earth%20tones%2C%20honest%20documentary%20style%2C%20magazine%20photography&width=1800&height=900&seq=casaflor-story-cover&orientation=landscape"
                alt="Três artesãs no ateliê da Casa Flor"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <p className="mt-3 text-[12px] italic text-foreground-500 text-center">
              O ateliê em Goiânia, uma manhã de terça no início da primavera.
            </p>
          </div>
        </section>

        {/* Pull quote */}
        <section className="w-full px-4 md:px-8 py-14 bg-background-100/70 border-y border-background-200/60">
          <div className="max-w-[920px] mx-auto text-center">
            <i className="ri-double-quotes-l text-primary-500 text-5xl block mb-4"></i>
            <p className="font-heading text-foreground-950 text-3xl md:text-4xl leading-[1.25] tracking-[-0.01em]">
              A gente não faz muito. A gente só quer fazer bem feito.
            </p>
            <p className="mt-6 text-[12px] uppercase tracking-[0.2em] text-foreground-600">
              — Mariana, fundadora
            </p>
          </div>
        </section>

        {/* Chapters — editorial timeline */}
        <section className="w-full px-4 md:px-8 py-20">
          <div className="max-w-[980px] mx-auto">
            <p className="text-[11px] uppercase tracking-[0.24em] text-foreground-500 mb-2">
              Capítulos
            </p>
            <h2 className="font-heading text-foreground-950 text-4xl md:text-5xl tracking-[-0.01em] mb-14">
              Três anos, três salas.
            </h2>

            <div className="space-y-12">
              {chapters.map((c) => (
                <article
                  key={c.no}
                  className="grid grid-cols-12 gap-6 pb-12 border-b border-background-200/60 last:border-0"
                >
                  <div className="col-span-12 md:col-span-3">
                    <p className="font-heading text-primary-600 text-5xl md:text-6xl leading-none">
                      {c.no}
                    </p>
                    <p className="mt-2 text-[12px] uppercase tracking-[0.2em] text-foreground-500">
                      {c.year}
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-9">
                    <h3 className="font-heading text-foreground-950 text-2xl md:text-[28px] leading-snug mb-3">
                      {c.title}
                    </h3>
                    <p className="text-[16px] leading-relaxed text-foreground-700">
                      {c.body}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Numbers strip */}
        <section className="w-full px-4 md:px-8 py-16 bg-foreground-950 text-background-50">
          <div className="max-w-[1180px] mx-auto">
            <p className="text-[11px] uppercase tracking-[0.24em] text-background-50/60 mb-8">
              Em números
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 md:gap-6">
              {numbers.map((n) => (
                <div key={n.label}>
                  <p className="font-heading text-[64px] md:text-[88px] leading-none tracking-[-0.02em]">
                    {n.value}
                  </p>
                  <p className="mt-3 text-[12px] uppercase tracking-[0.2em] text-background-50/60">
                    {n.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="w-full px-4 md:px-8 py-20">
          <div className="max-w-[820px] mx-auto text-center">
            <p className="font-heading text-foreground-950 text-3xl md:text-4xl leading-[1.25] tracking-[-0.01em] mb-6">
              Quer ver o que estamos produzindo esta semana?
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Visitar a loja
              <i className="ri-arrow-right-line"></i>
            </Link>
            <p className="mt-10 text-[11px] uppercase tracking-[0.24em] text-foreground-500">
              — Fim da Edição 03 —
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}